import type { RefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Player } from '@vimeo/player';
import { importWithRetry } from '@shared/core/chunkRetry';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { addAction, addError } from '@shared/utils/logger';
import { isBotClient } from '@shared/utils/botDetection/isBotClient';
import { noop } from '@shared/utils/noop';
import type { VimeoStallDetection, VimeoStallFailureReason, VimeoStallLikelyCause, VimeoStallOutcome, VimeoStallReport, VimeoStallStatus } from './useVimeoPlaybackSignal.types';

/** New Relic PageAction name; fields arrive prefixed `VimeoPlaybackStall_` (see `addAction`). */
export const VIMEO_STALL_ACTION_NAME = 'VimeoPlaybackStall';
const DEFAULT_STALL_TIMEOUT_MS = 3000;

type UseVimeoPlaybackSignalArgs = Readonly<{
  /** Attach only when the iframe hosts a player.vimeo.com embed. */
  enabled: boolean;
  /** Identity of the embedded video (URL or id); re-binds the player when it changes. */
  videoKey: string | undefined;
  onPlaying: () => void;
  onError: () => void;
  /** Opt in to stall detection, the tap-to-play retry and its telemetry. Omit for a plain playback signal. */
  stallDetection?: VimeoStallDetection;
}>;

type UseVimeoPlaybackSignalResult = Readonly<{
  iframeRef: RefObject<HTMLIFrameElement>;
  /** Always `idle` without `stallDetection`. Resets to `idle` when `videoKey` changes. */
  stallStatus: VimeoStallStatus;
  /**
   * Retries playback after a stall. Call it synchronously from the guest's tap/keypress handler:
   * `player.play()` must run inside the user gesture for iOS/Safari to allow it. No-op unless
   * `stallStatus` is `stalled`, so early and repeat taps never call `play()` twice.
   * The video should still be revealed by `onPlaying`, not by this call.
   */
  playAfterStall: () => void;
}>;

/**
 * Reveals a Vimeo background video only when frames are actually rendering.
 * The iframe `load` event is the wrong signal twice over: it fires when the embed
 * document loads (well before playback, hiding the poster over a black player),
 * and for an SSR'd iframe it can fire before hydration so React never sees it and
 * the video stays hidden forever. The player postMessage handshake has neither problem.
 *
 * With `stallDetection`, it also notices when a connected, visible player's playback time hasn't
 * moved within the timeout, or the player errors first (usually blocked autoplay). It exposes
 * `stallStatus` + `playAfterStall` so the caller can offer a tap-to-play button, and reports
 * `VimeoPlaybackStall` PageActions to New Relic: one `stalled` report per stall and at most one
 * `resolved` report. When playback time does move it calls `onPlaying`, so a caller that hid the
 * video on `onError` gets it back. Bots never stall or report.
 * Player `error` events are always logged via `addError`.
 */
export const useVimeoPlaybackSignal = ({ enabled, videoKey, onPlaying, onError, stallDetection }: UseVimeoPlaybackSignalArgs): UseVimeoPlaybackSignalResult => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const handlePlaying = useEventCallback(onPlaying);
  const handleError = useEventCallback(onError);
  const handleReport = useEventCallback((report: VimeoStallReport) => stallDetection?.onReport?.(report));
  const [stallStatus, setStallStatus] = useState<VimeoStallStatus>('idle');
  const playAfterStallRef = useRef<() => void>(noop);
  const playAfterStall = useCallback(() => playAfterStallRef.current(), []);
  // The connected player, shared with the stall effect so toggling stallDetection never re-binds it.
  const [player, setPlayer] = useState<Player | null>(null);
  // Per-video error flag and stall hook-in: an error can fire before stall detection is switched on.
  const sawPlayerErrorRef = useRef(false);
  const onStallPlayerErrorRef = useRef<() => void>(noop);

  const stallSurface = stallDetection?.surface;
  const stallTimeoutMs = stallDetection?.timeoutMs ?? DEFAULT_STALL_TIMEOUT_MS;
  const stallIsPreview = stallDetection?.isPreview ?? false;

  useEffect(() => {
    setPlayer(null);
    sawPlayerErrorRef.current = false;
    const iframe = iframeRef.current;
    if (!enabled || !videoKey || !iframe) return;

    let connected: Player | null = null;
    let cancelled = false;

    const onPlayerError = (error: unknown) => {
      addError(error, { context: 'useVimeoPlaybackSignal#playerError' });
      sawPlayerErrorRef.current = true;
      handleError();
      onStallPlayerErrorRef.current();
    };

    importWithRetry(
      () =>
        import(
          /* webpackChunkName: "vimeo-player" */
          '@vimeo/player'
        ),
      'vimeo-player',
    )
      .then(({ default: VimeoPlayer }) => {
        if (cancelled) return;
        connected = new VimeoPlayer(iframe);
        connected.on('playing', handlePlaying);
        connected.on('error', onPlayerError);
        // The embed can already be mid-playback by the time this chunk loads,
        // and 'playing' won't re-fire for a video that's already running.
        connected.getPaused().then(
          paused => {
            if (!cancelled && !paused) handlePlaying();
          },
          error => {
            addError(error, { context: 'useVimeoPlaybackSignal#getPaused' });
            // State unknown: reveal rather than risk staying hidden forever.
            if (!cancelled) handlePlaying();
          },
        );
        setPlayer(connected);
      })
      .catch(error => {
        addError(error, { context: 'useVimeoPlaybackSignal#loadPlayerLib' });
        // Without the API we can't see playback start; reveal so the video is never stuck hidden.
        if (!cancelled) handlePlaying();
      });

    return () => {
      cancelled = true;
      // No destroy(): the lib tears down the React-owned iframe. off() is enough.
      connected?.off('playing', handlePlaying);
      connected?.off('error', onPlayerError);
    };
  }, [enabled, videoKey, handlePlaying, handleError]);

  useEffect(() => {
    // A new video, or detection switched off, starts a fresh cycle; a stale `stalled` would show the button over it.
    setStallStatus('idle');
    // Headless renders (incl. our snapshot service) never autoplay; counting them would swamp the numbers.
    if (!player || !stallSurface || isBotClient()) return;
    const activePlayer = player;
    const surface = stallSurface;

    let cancelled = false;
    // Stall cycle state lives in the effect closure so player callbacks and the tap handler
    // read it synchronously; `stallStatus` state only mirrors `status` for rendering.
    let status: VimeoStallStatus = 'idle';
    // Stalls are judged on playback time actually moving, never on `playing`/`getPaused()`:
    // with autoplay blocked (iOS Low Power Mode) the embed can claim to play while frozen, or error instead.
    let hasProgressed = false;
    let isPlayerReady = false;
    let baselineSeconds: number | undefined;
    let stalledAt = 0;
    let likelyCause: VimeoStallLikelyCause = 'unknown';
    let stallTimer: ReturnType<typeof setTimeout> | undefined;
    let tapTimer: ReturnType<typeof setTimeout> | undefined;

    const updateStatus = (next: VimeoStallStatus) => {
      status = next;
      if (!cancelled) setStallStatus(next);
    };

    const report = (fields: Readonly<{ phase: VimeoStallReport['phase']; outcome?: VimeoStallOutcome; failureReason?: VimeoStallFailureReason }>) => {
      if (cancelled) return;
      const payload: VimeoStallReport = {
        ...fields,
        surface,
        likelyCause,
        timeoutMs: stallTimeoutMs,
        isPreview: stallIsPreview,
        ...(fields.phase === 'resolved' ? { msSinceStall: Date.now() - stalledAt } : {}),
      };
      addAction(VIMEO_STALL_ACTION_NAME, payload);
      handleReport(payload);
    };

    const onProgress = () => {
      if (cancelled || hasProgressed) return;
      hasProgressed = true;
      clearTimeout(stallTimer);
      clearTimeout(tapTimer);
      activePlayer.off('timeupdate', onTimeUpdate);
      if (status === 'stalled') report({ phase: 'resolved', outcome: 'selfRecovered' });
      if (status === 'retrying') report({ phase: 'resolved', outcome: 'tapFixed' });
      // `failed` already sent its resolved report; a late start just clears the state without a second one.
      if (status !== 'idle') updateStatus('idle');
      // Frames are moving: reveal even if `playing` was missed or an earlier `error` hid the video.
      handlePlaying();
    };

    // Fast path; the timeout's currentTime comparison covers embeds that don't emit it.
    const onTimeUpdate = (data: { seconds?: number } | undefined) => {
      if ((data?.seconds ?? 0) > 0) onProgress();
    };

    const declareStall = (cause: VimeoStallLikelyCause) => {
      // The probe is async: playback may have started, or the effect torn down, while it ran.
      if (cancelled || hasProgressed || status !== 'idle') return;
      clearTimeout(stallTimer);
      likelyCause = cause;
      stalledAt = Date.now();
      updateStatus('stalled');
      // Sent at once so guests who leave without tapping are still counted.
      report({ phase: 'stalled' });
    };

    // Blocked autoplay can surface as an error; offer the tap now rather than leave a dead poster.
    const onPlayerError = () => {
      if (document.visibilityState !== 'hidden') declareStall('playerError');
    };

    const probeLikelyCause = async (): Promise<VimeoStallLikelyCause | 'progressed'> => {
      if (sawPlayerErrorRef.current) return 'playerError';
      // ready() is tracked from connect time because an embed that never loads never settles it.
      if (!isPlayerReady) return 'embedNotReady';
      const seconds = await activePlayer.getCurrentTime();
      // Any change counts, including a loop wrapping back towards 0.
      if (baselineSeconds !== undefined && seconds !== baselineSeconds) return 'progressed';
      const paused = await activePlayer.getPaused();
      return paused ? 'autoplayBlocked' : 'frozenWhilePlaying';
    };

    const onStallTimeout = () => {
      if (cancelled || hasProgressed) return;
      probeLikelyCause().then(
        cause => (cause === 'progressed' ? onProgress() : declareStall(cause)),
        () => declareStall('unknown'),
      );
    };

    // The clock only runs while the tab is visible: browsers hold autoplay in background tabs,
    // so a guest opening the site in a new tab would otherwise count as a stall.
    const startStallClock = () => {
      clearTimeout(stallTimer);
      if (cancelled || hasProgressed || status !== 'idle') return;
      if (document.visibilityState === 'hidden') return;
      stallTimer = setTimeout(onStallTimeout, stallTimeoutMs);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') clearTimeout(stallTimer);
      else startStallClock();
    };

    const failTap = (reason: VimeoStallFailureReason) => {
      if (cancelled || status !== 'retrying') return;
      updateStatus('failed');
      report({ phase: 'resolved', outcome: 'tapFailed', failureReason: reason });
    };

    playAfterStallRef.current = () => {
      if (cancelled || status !== 'stalled') return;
      updateStatus('retrying');
      // Called synchronously from the tap so play() counts as part of the guest's gesture.
      activePlayer.play().then(
        () => {
          // play() can resolve after progress already moved us back to idle.
          if (cancelled || status !== 'retrying') return;
          tapTimer = setTimeout(() => failTap('noProgressAfterTap'), stallTimeoutMs);
        },
        () => failTap('playRejected'),
      );
    };

    onStallPlayerErrorRef.current = onPlayerError;
    activePlayer.on('timeupdate', onTimeUpdate);
    activePlayer.ready().then(
      () => {
        isPlayerReady = true;
      },
      // A failed embed surfaces through the error event; here it only means "not ready".
      noop,
    );
    activePlayer.getCurrentTime().then(seconds => {
      baselineSeconds = seconds;
    }, noop);
    document.addEventListener('visibilitychange', onVisibilityChange);
    // An error before detection switched on (flag loaded late) is still a stall.
    if (sawPlayerErrorRef.current) onPlayerError();
    else startStallClock();

    return () => {
      cancelled = true;
      clearTimeout(stallTimer);
      clearTimeout(tapTimer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      playAfterStallRef.current = noop;
      onStallPlayerErrorRef.current = noop;
      activePlayer.off('timeupdate', onTimeUpdate);
    };
  }, [player, handlePlaying, handleReport, stallSurface, stallTimeoutMs, stallIsPreview]);

  return { iframeRef, stallStatus, playAfterStall };
};
